import React, { useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Box } from "@mui/material";

// Components
import NeonProgressBar from "./components/NeonProgressBar";
import VideoIntro from "./components/VideoIntro";
import Flip from "./components/Flip";
import ProjectsDashboard from "./components/Dashboard";
import SWM from "./components/SWM";
import SuthraPunjab from "./SuthraPunjab/SuthraPunjab";

import AirQualityMenu from "./AirQuality/MainMenu";
import WildLifeMenu from "./WildLife/MainMenu";
import EMobilityPage from "./EMobility/EMobilityPage";

import "./App.css";

// Main Content Component that handles the animation sequence
const MainContent = () => {
  const location = useLocation();
  const isRootPath = location.pathname === "/";

  const [showNeonProgress, setShowNeonProgress] = useState(isRootPath);
  const [showVideo, setShowVideo] = useState(false);
  const [showFlip, setShowFlip] = useState(false);
  const [showProjects, setShowProjects] = useState(!isRootPath);

  const [videoOpacity, setVideoOpacity] = useState(0); // Start at 0 for smooth fade in
  const [flipOpacity, setFlipOpacity] = useState(0);

  // Neon Progress Bar transition start handler
  const handleNeonTransitionStart = () => {
    console.log("Neon progress bar transition started");
    // Start showing video with fade in transition
    setShowVideo(true);
    setVideoOpacity(1);
  };

  // Neon Progress Bar completion handler
  const handleNeonComplete = () => {
    console.log("Neon progress bar completed");
    // Hide progress bar completely after transition
    setShowNeonProgress(false);
  };

  // Start transition 3 seconds before video ends (at 14 seconds for a 17-second video)
  React.useEffect(() => {
    if (showVideo) {
      const transitionTimer = setTimeout(() => {
        console.log("Starting video to flip transition");
        // Start fade out transition for video and fade in for flip
        setVideoOpacity(0);
        setFlipOpacity(1);

        // Show flip component
        setShowFlip(true);
      }, 15000); // Start transition at 14 seconds (3 seconds before end)

      const endTimer = setTimeout(() => {
        console.log("Video ended, hiding video component");
        setShowVideo(false);
      }, 18000); // Completely hide video at 17 seconds

      return () => {
        clearTimeout(transitionTimer);
        clearTimeout(endTimer);
      };
    }
  }, [showVideo]);

  const handleVideoEnd = () => {
    console.log("Video ended naturally");
    // This will be called by VideoIntro when video naturally ends
    setShowVideo(false);
    setShowFlip(true);
    setFlipOpacity(1);
  };

  const handleFlipEnd = () => {
    console.log("Flip animation ended");
    setShowFlip(false);
    setShowProjects(true);
  };

  const handleSkipVideo = () => {
    console.log("Video skipped by user");
    setShowVideo(false);
    setShowFlip(true);
    setFlipOpacity(1);
  };

  const handleVideoClick = () => {
    console.log("Video clicked, skipping to flip");
    handleSkipVideo();
  };

  // Single Routes configuration for all paths
  const renderRoutes = () => (
    <Routes>
      <Route path="/" element={<ProjectsDashboard />} />
      <Route path="/SWM" element={<SWM />} />
      <Route path="/SuthraPunjab" element={<SuthraPunjab />} />
      <Route path="/AirQualityMenu" element={<AirQualityMenu />} />
      <Route path="/WildLifeMenu" element={<WildLifeMenu />} />
      <Route path="/PCAP" element={<EMobilityPage />} />
    </Routes>
  );

  // If not on root path, show the routes directly
  if (!isRootPath) {
    return <>{renderRoutes()}</>;
  }

  return (
    <>
      {/* Neon Progress Bar - No wrapper opacity, handled internally */}
      {showNeonProgress && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 10001,
          }}
        >
          <NeonProgressBar
            onComplete={handleNeonComplete}
            onTransitionStart={handleNeonTransitionStart}
          />
        </Box>
      )}

      {/* Video Intro with opacity transition */}
      {showVideo && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 9999,
            opacity: videoOpacity,
            transition: "opacity 1s ease-in-out",
          }}
        >
          <VideoIntro onVideoEnd={handleVideoEnd} onSkip={handleVideoClick} />
        </Box>
      )}

      {/* Flip component with opacity transition */}
      {showFlip && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 9998,
            opacity: flipOpacity,
            transition: "opacity 3s ease-in-out",
          }}
        >
          <Flip onFlipEnd={handleFlipEnd} />
        </Box>
      )}

      {/* Projects Dashboard - Only show when animation completes, without nested Routes */}
      {showProjects && <ProjectsDashboard />}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <MainContent />
    </BrowserRouter>
  );
}

export default App;
